/**
 * Browser-side capture, framework-free. Works in any SPA (Vite, CRA, plain HTML).
 *
 * The page never holds the connection token — it is a credential, and anything shipped to
 * a browser is public. The collector posts to the host's own relay endpoint (which forwards
 * with the token), so throttling and fingerprinting happen once, server-side, for all
 * visitors together.
 */

export type BrowserReport = {
  message: string;
  stack?: string;
  url: string;
  userAgent: string;
  /** Free label the host can use to tell surfaces apart ("dashboard", "wizard"). */
  source?: string;
  context?: Record<string, unknown>;
};

export type CollectorOptions = {
  /** Relay endpoint on the host server, e.g. `/api/memo-project/report`. */
  endpoint?: string;
  source?: string;
  /** Extra context attached to every report (release, user role, …). */
  context?: () => Record<string, unknown>;
  /** Same-fingerprint silence in the browser, before the server throttle. Default 30 s. */
  dedupeMs?: number;
  fetchImpl?: typeof fetch;
  /**
   * While true nothing is sent: e.g. during an update in progress, when failing requests
   * and reloads are expected. Read at every report.
   */
  paused?: () => boolean;
};

const DEFAULT_ENDPOINT = "/api/memo-project/report";

/** Posts one browser anomaly to the relay. Never throws, never awaits. */
export const createBrowserReporter = (options: CollectorOptions = {}) => {
  const endpoint = options.endpoint ?? DEFAULT_ENDPOINT;
  const fetchImpl = options.fetchImpl ?? (typeof fetch === "function" ? fetch : undefined);
  const dedupeMs = options.dedupeMs ?? 30_000;
  const recent = new Map<string, number>();

  return (message: string, stack?: string, context?: Record<string, unknown>): void => {
    try {
      if (!fetchImpl) return;
      if (options.paused?.()) return;
      const key = `${message}|${(stack ?? "").split("\n")[1] ?? ""}`;
      const now = Date.now();
      const last = recent.get(key);
      if (last !== undefined && now - last < dedupeMs) return;
      recent.set(key, now);
      if (recent.size > 200) recent.clear();

      const body: BrowserReport = {
        message: (message || "Errore JavaScript").slice(0, 500),
        stack: stack?.slice(0, 8_000),
        url: typeof location !== "undefined" ? location.href : "",
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        source: options.source,
        context: { ...(options.context?.() ?? {}), ...(context ?? {}) },
      };
      // `keepalive` lets the request survive the page being closed — exactly when a fatal
      // error tends to happen.
      void fetchImpl(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify(body),
      }).catch(() => {});
    } catch {
      // Never let the reporter break the page it is watching.
    }
  };
};

/**
 * An error thrown by a script from another origin (a browser extension, Safari's translator,
 * an in-app browser's injected code): the browser hides everything and leaves only
 * "Script error." with no error object, no line and no stack. Nothing in it can be traced
 * back to the application.
 */
export const isOpaqueScriptError = (
  event: Pick<ErrorEvent, "message" | "error" | "lineno">,
): boolean => event.message === "Script error." && !event.error && !event.lineno;

/**
 * Listens to what actually escapes in a browser application — uncaught errors and rejected
 * promises nobody handled — and reports them. Returns the uninstall function.
 *
 * Failed resource loads fire `error` too but carry no message: they are noise, not
 * application faults, and are skipped. So are opaque cross-origin errors
 * ({@link isOpaqueScriptError}).
 */
export const installErrorCollector = (options: CollectorOptions = {}): (() => void) => {
  if (typeof window === "undefined") return () => {};
  const send = createBrowserReporter(options);

  const onError = (event: ErrorEvent) => {
    if (!event.message && !event.error) return;
    if (isOpaqueScriptError(event)) return;
    send(event.message || "Errore JavaScript", event.error?.stack);
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    send(
      reason instanceof Error ? reason.message : `Promise rifiutata: ${String(reason)}`,
      reason instanceof Error ? reason.stack : undefined,
    );
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
};

/** For `catch` blocks that handle an error but still want it on the dashboard. */
export const captureException = (
  error: unknown,
  options: CollectorOptions & { context?: () => Record<string, unknown> } = {},
): void => {
  const send = createBrowserReporter(options);
  if (error instanceof Error) send(error.message, error.stack);
  else send(String(error));
};
